/**
 * El CSS de la hoja ISO, como texto: la web lo inyecta en una etiqueta `<style>` y el worker lo
 * mete en el HTML que le da a Chromium. Es CSS plano a propósito —sin Tailwind, sin variables
 * externas— porque tiene que verse igual en un navegador con la app cargada y en un Chromium
 * pelado que solo conoce este documento.
 *
 * Copia el formato de los documentos reales de SMG (GES-PRG-009): papel carta, Times New Roman
 * a 12 pt justificado, encabezado en tabla con bordes negros, títulos de sección en mayúsculas,
 * y el pie legal a 9 pt. Los colores son los del documento: azul #2e74b5 para la revisión y
 * el nombre de la empresa, rojo #c00000 para la línea de confidencialidad.
 */
export const CSS_HOJA = `
/*
 * Márgenes de impresión. El bloque legal va SOLO en la primera página, como en el Word de SMG:
 * la primera reserva 56 mm abajo para que Chromium lo dibuje ahí (footerTemplate), y las demás
 * dejan el margen normal. El worker imprime en dos pasadas con el mismo HTML —la página 1 con
 * el pie legal y el resto sin pie— y las une; como las reglas @page son las mismas en las dos,
 * la paginación coincide.
 */
@page {
  size: letter;
  margin: 30mm 20mm 20mm 20mm;
}
@page :first {
  margin-bottom: 56mm;
}

.hoja {
  font-family: "Times New Roman", Times, serif;
  font-size: 12pt;
  line-height: 1.25;
  color: #000000;
  background: #ffffff;
  position: relative;
}

/* En pantalla la hoja se ve como una hoja: ancho carta, sombra, márgenes, y el pie abajo. */
.hoja[data-modo="pantalla"] {
  width: 216mm;
  min-height: 279mm;
  padding: 18mm 20mm 14mm;
  margin: 0 auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 27, 104, 0.08);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.hoja[data-modo="pantalla"] .hoja-cuerpo { flex: 1 0 auto; }

/*
 * Impresa, la hoja mide exactamente lo que cabe entre los márgenes de @page (216 − 20 − 20 mm).
 * Sin este ancho, Chromium maqueta al ancho del viewport (1280 px) y encoge todo para que
 * quepa: el texto salía a 8 pt en vez de 12.
 */
.hoja[data-modo="impresion"] { width: 176mm; margin: 0; }
@media print {
  html, body { margin: 0; padding: 0; }
}

/* Marca de agua del borrador: la vista previa PDF sale sin sello y lo dice en grande. */
.hoja[data-borrador="true"]::before {
  content: "BORRADOR";
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Arial, sans-serif;
  font-size: 96pt;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: rgba(0, 27, 104, 0.07);
  transform: rotate(-30deg);
  pointer-events: none;
  z-index: 0;
}
.hoja > * { position: relative; z-index: 1; }
/*
 * Impreso, la marca de agua va en cada página: Chromium repite en todas lo que está fijo. El
 * color va sin transparencia porque no todos los visores de PDF la respetan igual.
 */
@media print {
  .hoja[data-borrador="true"]::before {
    position: fixed;
    inset: 0;
    color: #d9e0ee;
  }
}

/* ---------- Encabezado (en pantalla; en el PDF lo dibuja Chromium con el mismo HTML) ---------- */
.hoja-encabezado { margin-bottom: 6mm; }
.hoja-encabezado-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 12pt;
}
.hoja-encabezado-tabla td, .hoja-encabezado-tabla th {
  border: 1pt solid #000000;
  padding: 0.6mm 2mm;
  vertical-align: middle;
}
.hoja-encabezado-tabla th { font-weight: 400; text-align: center; width: 20mm; }
.hoja-logo { width: 56mm; text-align: center; padding: 1.5mm 3mm !important; }
.hoja-logo img { width: 50mm; height: auto; display: block; margin: 0 auto; }
.hoja-logo-marcador { font-family: Arial, sans-serif; font-weight: 700; font-size: 22pt; color: #1a1f4e; }
.hoja-titulo { text-align: center; font-weight: 700; font-size: 14pt; text-transform: uppercase; }
.hoja-dato { text-align: center; font-weight: 700; }
.hoja-rev { color: #2e74b5; }

/* ---------- Cuerpo ---------- */
.hoja-cuerpo { text-align: justify; counter-reset: seccion; }
/* Las secciones las numera React (el "1." del título); el contador solo alimenta a los subtítulos. */
.hoja-seccion { margin-bottom: 5mm; counter-increment: seccion; counter-reset: sub; }
.hoja-seccion > h1 {
  font-size: 14pt;
  font-weight: 700;
  text-transform: uppercase;
  margin: 0 0 1mm;
  break-after: avoid;
  text-align: left;
}
.hoja-seccion > h1 .hoja-n { display: inline-block; min-width: 9mm; }
.hoja-cuerpo h2 {
  font-size: 12pt;
  font-weight: 700;
  margin: 3mm 0 1mm;
  break-after: avoid;
  counter-increment: sub;
  counter-reset: subsub;
  text-align: left;
}
/* Los subtítulos se numeran solos, como en SMG: 5.1, 5.2 y 5.2.1. */
.hoja-cuerpo h2::before { content: counter(seccion) "." counter(sub) " "; }
.hoja-cuerpo h3 {
  font-size: 12pt;
  font-weight: 700;
  margin: 2mm 0 1mm;
  break-after: avoid;
  counter-increment: subsub;
  text-align: left;
}
.hoja-cuerpo h3::before { content: counter(seccion) "." counter(sub) "." counter(subsub) " "; }
.hoja-cuerpo p { margin: 0 0 2mm; }
.hoja-cuerpo ul { margin: 0 0 2mm; padding-left: 10mm; list-style: disc; }
.hoja-cuerpo li > p { margin: 0 0 0.5mm; }
.hoja-cuerpo table { border-collapse: collapse; width: 100%; margin: 1mm 0 3mm; font-size: 11pt; }
.hoja-cuerpo th, .hoja-cuerpo td { border: 1pt solid #000000; padding: 1mm 2mm; vertical-align: top; text-align: left; }
.hoja-cuerpo th { font-weight: 700; text-align: center; }
.hoja-cuerpo tr { break-inside: avoid; }
/* Una tabla de diez columnas o más (GES-REG-001: una persona por fila, una casilla por punto) va
   en letra chica, como en el papel; con 11 pt los encabezados no caben y se pierden los anchos. */
.hoja-cuerpo table:has(> tbody > tr:first-child > :nth-child(10)) { font-size: 9pt; }
.hoja-cuerpo table:has(> tbody > tr:first-child > :nth-child(10)) :is(th, td) { padding: 1mm 1mm; }
.hoja-cuerpo td > p:last-child, .hoja-cuerpo th > p:last-child { margin-bottom: 0; }
/* Un párrafo vacío en una celda guarda su renglón: es el espacio donde se escribe a mano. */
.hoja-cuerpo td > p:empty::before { content: "\\00a0"; }
/*
 * La tabla de firmas (variante "firmas"): como en el papel, sin cajas. Cada celda es una línea
 * de firma: renglones en blanco para firmar, la raya, y debajo el cargo y su código, centrados.
 * La raya va sobre el penúltimo párrafo (el cargo); el último es el código.
 */
.hoja-cuerpo table.hoja-tabla-firmas { margin-top: 4mm; }
.hoja-cuerpo table.hoja-tabla-firmas td { border: 0; text-align: center; vertical-align: bottom; padding: 0 8mm; }
.hoja-cuerpo table.hoja-tabla-firmas td > p { margin: 0; }
.hoja-cuerpo table.hoja-tabla-firmas td > p:nth-last-child(2) { border-top: 1pt solid #000000; padding-top: 1mm; margin-top: 1mm; }
/* La tabla sin bordes (variante "sinBordes"): texto en columnas, como el bloque de fecha y leyenda del Excel. */
.hoja-cuerpo table.hoja-tabla-sin-bordes :is(th, td) { border: 0; vertical-align: bottom; }
.hoja-cuerpo table.hoja-tabla-sin-bordes th { background: none !important; text-align: left; }
.hoja-centrado { text-align: center !important; }
.hoja-vacio { text-align: center !important; font-style: italic; color: #555555; }

/* La lista de pasos: número, qué se hace y quién. El número lo pone el CSS, no el contenido. */
.hoja-pasos { counter-reset: paso; }
.hoja-pasos .hoja-paso-n { width: 8mm; text-align: center; }
.hoja-pasos .hoja-paso-n::before { counter-increment: paso; content: counter(paso); }
.hoja-pasos .hoja-paso-quien { width: 40mm; }

.hoja-actualizaciones th { font-size: 10.5pt; }
.hoja-actualizaciones td { font-size: 10.5pt; }
/* La descripción del cambio respeta sus renglones: puede ser el historial de cambios entero. */
.hoja-actualizaciones td:last-child { white-space: pre-line; }

.hoja-nota {
  border: 1pt solid #000000;
  padding: 2mm 3mm;
  margin: 1mm 0 3mm;
  break-inside: avoid;
}
.hoja-nota[data-tono="advertencia"] { border-width: 1.5pt; }
.hoja-nota-etiqueta { display: inline; font-weight: 700; margin-right: 2mm; }
.hoja-nota-etiqueta::after { content: ":"; }
.hoja-nota p { display: inline; margin: 0; }

.hoja-figura { margin: 1mm 0 3mm; text-align: center; break-inside: avoid; }
.hoja-figura img { max-width: 100%; height: auto; }
.hoja-figura figcaption { font-size: 10pt; margin-top: 1mm; }
.hoja-flujograma { text-align: center; margin: 1mm 0 3mm; }
.hoja-flujograma svg { max-width: 100%; height: auto; }
.hoja-flujograma-pendiente {
  border: 1pt dashed #777777;
  color: #555555;
  font-size: 10pt;
  text-align: center;
  padding: 6mm;
  margin: 1mm 0 3mm;
}

/*
 * La lista de verificación de un formato, como GES-REG-022: el área a la izquierda y los puntos
 * en filas de siete; debajo de cada fila de nombres, la fila en blanco donde se marca a mano.
 */
.hoja-cuerpo table.hoja-verificacion { table-layout: fixed; font-size: 9pt; line-height: 1.15; margin: 0 0 3mm; }
.hoja-verificacion-col-area { width: 17%; }
.hoja-cuerpo .hoja-verificacion th.hoja-verificacion-nombre { text-align: left; vertical-align: middle; font-weight: 700; }
.hoja-cuerpo .hoja-verificacion td { padding: 0.8mm 1.2mm; font-weight: 700; }
.hoja-cuerpo .hoja-verificacion .hoja-verificacion-marcas td { height: 7mm; padding: 0; }
.hoja-verificacion-area { break-inside: avoid; }

/* La hoja apaisada (fase 3b): carta horizontal en pantalla y en el PDF. */
.hoja[data-modo="pantalla"][data-orientacion="horizontal"] { width: 279.4mm; min-height: 216mm; padding: 14mm 16mm 12mm; }
.hoja[data-modo="impresion"][data-orientacion="horizontal"] { width: 239.4mm; }

/*
 * La letra compacta: formatos hechos en Excel que en papel caben en una sola hoja con letra
 * chica, como GES-REG-001 (criterios, once personas, acciones, verificación y firmas en una
 * carta apaisada). Baja la letra, los márgenes y los rellenos de las celdas; el pie legal va
 * chico también. El margen de abajo se queda en 11 mm: el corte en cartas de la hoja llena deja
 * 10 mm libres al pie, y con menos pediría una segunda carta por la raya del pie legal.
 */
.hoja[data-densidad="compacta"] { font-size: 7.5pt; line-height: 1.12; }
.hoja[data-modo="pantalla"][data-densidad="compacta"] { padding: 10mm 12mm 11mm; }
.hoja[data-modo="pantalla"][data-densidad="compacta"][data-orientacion="horizontal"] { padding: 5mm 10mm 11mm; }
.hoja[data-densidad="compacta"] .hoja-encabezado { margin-bottom: 1.5mm; }
.hoja[data-densidad="compacta"] .hoja-encabezado-tabla { font-size: 8pt; }
.hoja[data-densidad="compacta"] .hoja-encabezado-tabla td, .hoja[data-densidad="compacta"] .hoja-encabezado-tabla th { padding: 0.3mm 1.5mm; }
.hoja[data-densidad="compacta"] .hoja-logo { width: 38mm; padding: 0.8mm 2mm !important; }
.hoja[data-densidad="compacta"] .hoja-logo img { width: 30mm; }
.hoja[data-densidad="compacta"] .hoja-logo-marcador { font-size: 16pt; }
.hoja[data-densidad="compacta"] .hoja-titulo { font-size: 10pt; }
.hoja[data-densidad="compacta"] .hoja-seccion { margin-bottom: 2.5mm; }
.hoja[data-densidad="compacta"] .hoja-seccion > h1 { font-size: 10pt; }
.hoja[data-densidad="compacta"] .hoja-cuerpo h2, .hoja[data-densidad="compacta"] .hoja-cuerpo h3 { font-size: 8pt; margin: 1.5mm 0 0.5mm; }
.hoja[data-densidad="compacta"] .hoja-cuerpo p { margin: 0 0 0.8mm; }
.hoja[data-densidad="compacta"] .hoja-cuerpo ul { margin: 0 0 0.8mm; padding-left: 6mm; }
.hoja[data-densidad="compacta"] .hoja-cuerpo table { font-size: 6pt; line-height: 1.05; margin: 0.3mm 0 0.8mm; }
.hoja[data-densidad="compacta"] .hoja-cuerpo th, .hoja[data-densidad="compacta"] .hoja-cuerpo td { padding: 0.3mm 0.8mm; }
/* Los encabezados sombreados en gris, como las hojas hechas en Excel. */
.hoja[data-densidad="compacta"] .hoja-cuerpo th { background: #d9d9d9; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.hoja[data-densidad="compacta"] .hoja-cuerpo table:has(> tbody > tr:first-child > :nth-child(10)) { font-size: 6.8pt; line-height: 1.1; }
.hoja[data-densidad="compacta"] .hoja-cuerpo table:has(> tbody > tr:first-child > :nth-child(10)) :is(th, td) { padding: 0.3mm 0.6mm; }
/* Una fila por persona: alta como en el Excel impreso, para escribir con el lápiz. */
.hoja[data-densidad="compacta"] .hoja-cuerpo table:has(> tbody > tr:first-child > :nth-child(10)) td { height: 5mm; vertical-align: middle; }
/* Entre bloque y bloque queda aire, como las filas vacías del Excel. */
.hoja[data-densidad="compacta"] .hoja-cuerpo table { margin-bottom: 1.2mm; }
.hoja[data-densidad="compacta"] .hoja-cuerpo table.hoja-tabla-firmas { margin-top: 2mm; }
.hoja[data-densidad="compacta"] .hoja-cuerpo table.hoja-tabla-firmas td { padding: 0 20mm; }
.hoja[data-densidad="compacta"] .hoja-nota { padding: 1mm 1.5mm; margin: 0.5mm 0 1.5mm; }
.hoja[data-densidad="compacta"] .hoja-firmas { margin-top: 3mm; font-size: 7.5pt; }
.hoja[data-densidad="compacta"] .hoja-firmas td { height: 12mm; padding: 1mm; }
.hoja[data-densidad="compacta"] .hoja-legal { margin-top: 1mm; font-size: 5pt; line-height: 1.1; }
.hoja[data-densidad="compacta"] .hoja-legal p { margin: 0 0 0.4mm; }

/* Las referencias a otros documentos van entre paréntesis, como en SMG: (GES-REG-019). */
.hoja-referencia::before { content: "("; }
.hoja-referencia::after { content: ")"; }

/* Fase 4: las marcas del revisor (pantalla 09). El número va por CSS: no entra en el texto. */
.hoja mark.hoja-marca { color: inherit; padding: 0 1px; border-radius: 2px; cursor: pointer; background: #fbe9e7; box-shadow: inset 0 -2px 0 #b42318; }
.hoja mark.hoja-marca[data-tipo="sugerencia"] { background: #fbf1de; box-shadow: inset 0 -2px 0 #b7791f; }
.hoja mark.hoja-marca[data-tipo="comentario"] { background: #e6eefb; box-shadow: inset 0 -2px 0 #1d4ed8; }
.hoja mark.hoja-marca[data-numero]::before {
  content: attr(data-numero);
  display: inline-block; margin-right: 2px; padding: 0 3px; border-radius: 3px;
  font: 700 7.5pt/1.3 Arial, sans-serif; vertical-align: 0.45em; color: #ffffff; background: #b42318;
}
.hoja mark.hoja-marca[data-tipo="sugerencia"][data-numero]::before { background: #b7791f; }
.hoja mark.hoja-marca[data-tipo="comentario"][data-numero]::before { background: #1d4ed8; }
.hoja mark.hoja-marca[data-activa] { outline: 2px solid #1f3a6b; outline-offset: 1px; }

/* ---------- Pie de firmas (la evidencia digital; va una vez, al final del cuerpo) ---------- */
.hoja-firmas {
  width: 100%;
  border-collapse: collapse;
  margin-top: 6mm;
  font-size: 10pt;
  break-inside: avoid;
}
.hoja-firmas th { border: 1pt solid #000000; font-weight: 700; padding: 1mm; text-align: center; width: 33.33%; }
.hoja-firmas td { border: 1pt solid #000000; padding: 2mm; vertical-align: top; height: 20mm; text-align: left; }
.hoja-firma-imagen { display: block; max-height: 12mm; max-width: 40mm; margin: 0 auto 1mm; }
.hoja-firma-nombre { font-weight: 700; display: block; }
.hoja-firma-puesto { display: block; }
.hoja-firma-fecha { display: block; font-size: 9pt; }
.hoja-firma-id { display: block; font-size: 8.5pt; font-family: Consolas, "Courier New", monospace; }
.hoja-firma-pendiente { font-style: italic; color: #555555; }

/* ---------- Pie legal (en pantalla; en el PDF va en el pie de cada página) ---------- */
.hoja-legal {
  margin-top: 8mm;
  padding-top: 1mm;
  border-top: 1.5pt solid #000000;
  font-size: 9pt;
  line-height: 1.2;
  text-align: justify;
}
.hoja-legal p { margin: 0 0 1.5mm; }
.hoja-legal-titulo { text-align: right; font-weight: 700; }
.hoja-azul { color: #2e74b5; }
.hoja-legal-sistema { text-align: right; font-weight: 700; margin-bottom: 0 !important; }
.hoja-legal-confidencial { text-align: right; font-weight: 700; color: #c00000; margin-bottom: 0 !important; }
.hoja-legal-sello { text-align: right; font-family: Consolas, "Courier New", monospace; font-size: 8pt; color: #555555; margin-bottom: 0 !important; }
`;
